import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  checkUsernameAvailability,
  parseAuthMeProfile,
  register,
  sendRegistrationOtp,
  verifyRegistrationOtp
} from "./api/authApi";
import { sanitizeApiMessage } from "./auth/clientAuth";
import WillPlayMotionLogo from "./components/WillPlayMotionLogo";
import PasswordInput from "./PasswordInput";

function validateUsername(value) {
  const username = String(value || "").trim();
  if (!username) return "Enter a username.";
  if (username.length < 3 || username.length > 50) {
    return "Username must be between 3 and 50 characters.";
  }
  if (/^[._]|[._]$/.test(username)) {
    return "Username cannot start or end with dot or underscore.";
  }
  if (!/^[A-Za-z0-9._]+$/.test(username)) {
    return "Username can only use letters, numbers, underscore, and dot.";
  }
  return "";
}

function validateStrongPassword(value) {
  const password = String(value || "");
  if (password.length < 8) return "Password must be at least 8 characters.";
  if (/\s/.test(password)) return "Password cannot contain spaces.";
  if (!/[A-Z]/.test(password)) return "Password must include at least one uppercase letter.";
  if (!/[a-z]/.test(password)) return "Password must include at least one lowercase letter.";
  if (!/[0-9]/.test(password)) return "Password must include at least one number.";
  if (!/[^A-Za-z0-9]/.test(password)) return "Password must include at least one special character.";
  return "";
}

/** @param {{ onLoginSuccess?: (profile: { username: string, email?: string, userId?: number | null } | null) => void }} props */
function Register({ onLoginSuccess }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("verify-email");
  const [email, setEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [verificationToken, setVerificationToken] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState({
    checking: false,
    available: null,
    message: ""
  });

  const passwordMessage = useMemo(() => validateStrongPassword(password), [password]);
  const usernameStatusClass =
    usernameStatus.available === true
      ? "success-text"
      : usernameStatus.available === false
        ? "error-text"
        : "muted small";
  const showUsernameStatus = usernameTouched && username.trim().length > 0 && Boolean(usernameStatus.message);
  const showPasswordMessage = passwordTouched && password.length > 0 && Boolean(passwordMessage);
  const showConfirmPasswordMessage =
    confirmPasswordTouched && confirmPassword.length > 0 && password !== confirmPassword;
  const canRegister =
    !registering &&
    !usernameStatus.checking &&
    username.trim().length > 0 &&
    usernameStatus.available === true &&
    password.length > 0 &&
    confirmPassword.length > 0 &&
    !passwordMessage &&
    password === confirmPassword &&
    acceptedTerms;

  useEffect(() => {
    if (step !== "create-account") return undefined;

    const trimmed = username.trim();
    if (!trimmed) {
      setUsernameStatus({ checking: false, available: null, message: "" });
      return undefined;
    }

    const localValidation = validateUsername(trimmed);
    if (localValidation) {
      setUsernameStatus({ checking: false, available: false, message: localValidation });
      return undefined;
    }

    let cancelled = false;
    setUsernameStatus((prev) => ({ ...prev, checking: true, message: "Checking username..." }));

    const timeoutId = window.setTimeout(async () => {
      try {
        const { data } = await checkUsernameAvailability(trimmed);
        if (cancelled) return;

        const available = Boolean(data && typeof data === "object" && (data.available ?? data.Available) === true);
        const message =
          (data && typeof data === "object" && typeof data.message === "string" && data.message) ||
          (available ? "Username is available." : "That username is already taken.");

        setUsernameStatus({
          checking: false,
          available,
          message
        });
      } catch {
        if (cancelled) return;
        setUsernameStatus({
          checking: false,
          available: null,
          message: "Could not check username right now."
        });
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [step, username]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Enter your email.");
      return;
    }

    setSendingOtp(true);
    try {
      const { response, data } = await sendRegistrationOtp(trimmedEmail);
      if (!response.ok) {
        const raw =
          (data && typeof data === "object" && typeof data.message === "string" && data.message) ||
          "Could not send OTP.";
        setError(sanitizeApiMessage(raw));
        return;
      }

      setOtpSent(true);
      setInfo("OTP sent. Enter the 6-digit code below and verify.");
    } catch (apiError) {
      console.error(apiError);
      setError("Network error while sending OTP.");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");

    const code = otpCode.replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      setError("Enter the 6-digit OTP.");
      return;
    }

    setVerifyingOtp(true);
    try {
      const { response, data } = await verifyRegistrationOtp({
        email: email.trim(),
        code
      });

      if (!response.ok) {
        const raw =
          (data && typeof data === "object" && typeof data.message === "string" && data.message) ||
          "OTP verification failed.";
        setError(sanitizeApiMessage(raw));
        return;
      }

      const token =
        (data && typeof data === "object" && (data.verificationToken ?? data.VerificationToken)) || "";
      if (typeof token !== "string" || !token.trim()) {
        setError("Verification succeeded but the server did not return a token.");
        return;
      }

      setVerificationToken(token.trim());
      setStep("create-account");
      setInfo("");
      setError("");
    } catch (apiError) {
      console.error(apiError);
      setError("Network error while verifying OTP.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");

    const usernameValidation = validateUsername(username);
    if (usernameValidation) {
      setError(usernameValidation);
      return;
    }

    if (usernameStatus.available === false) {
      setError(usernameStatus.message || "That username is already taken.");
      return;
    }

    if (usernameStatus.checking) {
      setError("Please wait for username availability to finish checking.");
      return;
    }

    if (passwordMessage) {
      setError(passwordMessage);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!acceptedTerms) {
      setError("Please accept the Terms and Privacy Policy.");
      return;
    }

    setRegistering(true);
    try {
      const { response, data } = await register({
        email: email.trim(),
        username: username.trim(),
        password,
        confirmPassword,
        verificationToken
      });

      if (!response.ok) {
        const raw =
          (data && typeof data === "object" && typeof data.message === "string" && data.message) ||
          "Could not create your account.";
        setError(sanitizeApiMessage(raw));
        return;
      }

      const profile =
        data && typeof data === "object" ? parseAuthMeProfile(data) : null;

      onLoginSuccess?.(profile);
      navigate("/");
    } catch (apiError) {
      console.error(apiError);
      setError("Network error while creating your account.");
    } finally {
      setRegistering(false);
    }
  };

  return (
    <main className="auth-page">
      {step === "verify-email" ? (
        <form className="auth-card" onSubmit={otpSent ? handleVerifyOtp : handleSendOtp}>
          <div className="auth-brand">
            <WillPlayMotionLogo size={30} className="auth-brand-logo" />
            <span>WillPlay</span>
          </div>
          <h1>Create your account</h1>
          <p className="auth-step-hint">Enter your email, send OTP, then verify it to continue.</p>

          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (otpSent) {
                setOtpSent(false);
                setOtpCode("");
                setInfo("");
                setError("");
              }
            }}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
          />

          {!otpSent ? (
            <button
              className="primary-btn"
              type="submit"
              disabled={sendingOtp || verifyingOtp}
            >
              {sendingOtp ? "Sending OTP..." : "Send OTP"}
            </button>
          ) : null}

          {otpSent ? (
            <>
              <input
                className="otp-field"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Enter OTP"
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
              <button
                className="primary-btn"
                type="submit"
                disabled={verifyingOtp}
              >
                {verifyingOtp ? "Verifying..." : "Verify OTP"}
              </button>
              <div className="auth-resend-row">
                <button
                  className="ghost-btn small"
                  type="button"
                  onClick={handleSendOtp}
                  disabled={sendingOtp || verifyingOtp}
                >
                  {sendingOtp ? "Sending OTP..." : "Resend OTP"}
                </button>
              </div>
            </>
          ) : null}

          {info ? <p className="success-text">{info}</p> : null}
          {error ? <p className="error-text">{error}</p> : null}

          <p>
            Already have an account? <Link to="/login">Login</Link>
          </p>
        </form>
      ) : (
        <form className="auth-card" onSubmit={handleRegister}>
          <div className="auth-brand">
            <WillPlayMotionLogo size={30} className="auth-brand-logo" />
            <span>WillPlay</span>
          </div>
          <h1>Finish registration</h1>
          <p className="auth-step-hint">
            Your email is verified. Choose a unique username and a strong password.
          </p>
          <p className="auth-step-hint">
            Verified email: <span className="auth-email-highlight">{email}</span>
          </p>

          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => {
              setUsernameTouched(true);
              setUsername(e.target.value);
            }}
            onBlur={() => setUsernameTouched(true)}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            maxLength={50}
            required
          />

          {showUsernameStatus ? (
            <p className={usernameStatusClass}>
              {usernameStatus.message}
            </p>
          ) : null}

          <PasswordInput
            placeholder="Password"
            value={password}
            onChange={(e) => {
              setPasswordTouched(true);
              setPassword(e.target.value);
            }}
            onBlur={() => setPasswordTouched(true)}
            required
            autoComplete="new-password"
            name="password"
          />
          {showPasswordMessage ? (
            <p className="error-text">{passwordMessage}</p>
          ) : null}

          <PasswordInput
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPasswordTouched(true);
              setConfirmPassword(e.target.value);
            }}
            onBlur={() => setConfirmPasswordTouched(true)}
            required
            autoComplete="new-password"
            name="confirm-password"
          />

          {showConfirmPasswordMessage ? (
            <p className="error-text">Passwords do not match.</p>
          ) : null}

          {error ? <p className="error-text">{error}</p> : null}

          <label className="auth-terms-row">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
            />
            <span>
              I agree to the <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.
            </span>
          </label>

          <button className="primary-btn" type="submit" disabled={!canRegister}>
            {registering ? "Registering..." : "Register"}
          </button>

          <div className="auth-resend-row">
            <button
              type="button"
              className="ghost-btn small"
              onClick={() => {
                setStep("verify-email");
                setOtpCode("");
                setVerificationToken("");
                setUsername("");
                setPassword("");
                setConfirmPassword("");
                setUsernameTouched(false);
                setPasswordTouched(false);
                setConfirmPasswordTouched(false);
                setUsernameStatus({ checking: false, available: null, message: "" });
                setError("");
                setInfo("");
              }}
            >
              Back
            </button>
            <Link to="/login">Login</Link>
          </div>
        </form>
      )}
    </main>
  );
}

export default Register;
